import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Dialog,
    DialogTrigger,
    DialogSurface,
    DialogTitle,
    DialogBody,
    DialogContent,
    Button,
    makeStyles,
    Body1Strong,
    Card,
    Textarea,
    Field,
    Body1,
    Body2,
    Title3,
    Title2,
    SearchBox,
    Image,
    Subtitle2Stronger,
    CardHeader,
    Caption1,
    Text,
    tokens,
    Select,
    Option,
    SelectProps,
    TabList,
    Tab,
    Skeleton,
    SkeletonItem,
    SkeletonProps,
    Tooltip,
    Badge,
} from '@fluentui/react-components';
import { AddRegular, Info12Regular, Dismiss16Regular, Delete16Regular, Search16Regular } from '@fluentui/react-icons';
import TitleSelectGraphic from '../../titleSelectGraphic.png';
import type {
    SearchBoxChangeEvent,
    SelectTabData,
    SelectTabEvent,
    TabValue,
} from '@fluentui/react-components';
import { CopilotService } from '../../Service/ServerAPI';
import {
    FilterSearchResponse,
    ProductSearchResponse,
    SearchResults,
    SemanticSearchResponse,
    TitleResults,
    TitleSearchResponse,
} from '../../types/ArtifactTypes';
import { useTokenService } from '../../tokenService';
import { MoreHorizontal20Regular } from '@fluentui/react-icons';
import ContextTags from './ContextTags';
import {
    addToContext,
    clearContext,
    removeFromContext,
    RootState,
    setSelectedTab,
    toggleDialog,
} from '../../utils/store';
import SearchResultItem from './SearchResultItem';
import { useContext, useEffect } from 'react';
import { UserContext, useUser } from '../../utils/UserContext';

// Define the options
const options = [
    { key: 'title', text: 'Game/App' },
    { key: 'franchise', text: 'Franchise' },
    { key: 'series', text: 'Series' },
    { key: 'products', text: 'Products' },
    { key: 'subscriptions', text: 'Subscriptions' },
    //{ key: 'semantic', text: 'Semantic Search' },
    // ... other options
];

const useStyles = makeStyles({
    surface: {
        padding: 0,
        width: '1200px',
        maxWidth: '95vw',
        backgroundColor: '#1A1B1E',
    },
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        maxWidth: '100%',
    },
    header: {
        display: 'flex',
        justifyContent: 'center',
        backgroundColor: '#25282C',
        padding: '18px',
    },
    content: {
        display: 'flex',
        flexDirection: 'row',
        gap: '24px',
        width: '100%',
        maxWidth: '100%',
        alignItems: 'stretch',
    },
    searchSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        flex: '2',
        minWidth: '0',
    },
    contextPanel: {
        display: 'flex',
        flexDirection: 'column',
        width: '320px',
        flexShrink: 0,
        backgroundColor: '#25282C',
        borderRadius: '8px',
        padding: '16px',
        border: `1px solid ${tokens.colorNeutralStroke2}`,
    },
    contextHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '12px',
    },
    contextTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    contextList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        height: '500px',
        overflowY: 'auto',
        marginBottom: '16px',
        padding: '4px',
        flexShrink: 0,
        flexGrow: 2,
    },
    contextItem: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px',
        backgroundColor: '#1A1B1E',
        borderRadius: '4px',
        border: `1px solid ${tokens.colorNeutralStroke3}`,
    },
    contextItemName: {
        flex: '1',
        minWidth: '0',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    removeButton: {
        minWidth: '24px',
        height: '24px',
    },
    howToUse: {
        display: 'flex',
        flexDirection: 'column',
        width: '19.5vw',
        gap: '24px',
        backgroundColor: '#282B30',
        padding: '24px',
    },
    searchResultsContainer: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
        gridAutoRows: 'max-content',
        gap: '16px',
        backgroundColor: '#282B30',
        padding: '24px',
        overflowY: 'auto',
        height: '60vh',
        alignItems: 'start',
        alignContent: 'start',
    },
    descriptionDisplay: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        gap: '8px',
        width: '30vw',
        backgroundColor: '#282B30',
    },
    card: {
        padding: '16px',
        backgroundColor: '#1A1B1E',
        minHeight: '100px', // Ensure a minimum height
        //height: 'fit-content',
        display: 'flex',
        flexDirection: 'column',
        //justifyContent: 'space-between',
        justifyContent: 'flex-start',
        height: 'fit-content',
    },
    cardHeader: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start', // Align items to the top of the container
        // ... other styles you might have
    },
    cardTitle: {
        marginBottom: '8px',
    },
    horizontalCardImage: {
        width: '64px',
        height: '64px',
    },

    headerImage: {
        borderRadius: '4px',
        maxWidth: '60px',
        maxHeight: '60px',
    },

    caption: {
        color: tokens.colorNeutralForeground3,
    },

    text: { margin: '0' },

    searchSelectContainer: {
        display: 'flex',
        gap: '10px', // Adjust the spacing between the search box and select box
        alignItems: 'center', // Align items vertically
        // Set a min-width on both the SearchBox and Select containers if necessary
    },
    searchBox: {
        width: '100%',
        minWidth: '500px',
        fontSize: '16px',
        backgroundColor: '#2D3135',
        borderRadius: '8px',
        border: 'none',
    },
    searchBoxWrapper: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        marginBottom: '24px',
        backgroundColor: '#2D3135',
        borderRadius: '8px',
        border: '2px solid transparent',
    },
    searchIcon: {
        position: 'absolute',
        left: '16px',
        zIndex: 1,
        color: tokens.colorNeutralForeground3,
        pointerEvents: 'none',
    },

    headerTextContainer: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        minWidth: '250px',
        // Add any additional styling if needed
    },
    searchBoxContainer: {
        flexGrow: 1,
        flex: '1', // Allows the search box to fill the space
        minWidth: '300px',
    },

    selectContainer: {
        minWidth: '150px', // Set a minimum width for the select box or adjust as needed
    },
    fieldContainer: {
        width: '100%', // Ensure Field component takes up 100% of the available width
        overflow: 'hidden',
        boxSizing: 'border-box', // Include padding and border in the element's total width and height
        maxWidth: '100%', // Ensure it does not exceed its parent width
    },
    contextSection: {
        width: '100%',
        overflow: 'hidden',
        boxSizing: 'border-box',
        maxWidth: '100%',
        backgroundColor: '#25282C', // Slightly darker background to make context tags stand out
        padding: '16px',
        borderRadius: '4px',
        border: `1px solid ${tokens.colorNeutralStroke2}`,
    },
    secondThirdRow: {
        alignItems: 'center',
        display: 'grid',
        paddingBottom: '15px',
        position: 'relative',
        gap: '10px',
        gridTemplateColumns: 'min-content 60% 20%',
    },
    skeletonGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
        gridAutoRows: 'max-content',
        gap: '16px',
        alignItems: 'start',
        alignContent: 'start',
    },
    skeletonCard: {
        backgroundColor: '#1A1B1E',
        borderRadius: '8px',
        overflow: 'hidden',
        minHeight: '200px',
        display: 'flex',
        flexDirection: 'column',
    },
    skeletonImage: {
        width: '100%',
        aspectRatio: '1/1',
        flexShrink: 0,
    },
    skeletonContent: {
        padding: '12px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        flex: 1,
    },
});

// Debounce function to limit the rate of API calls
const useDebounce = (callback: (...args: any[]) => void, delay: number) => {
    const timeoutRef = React.useRef<number | null>(null);

    const debouncedFunction = (...args: any[]) => {
        if (timeoutRef.current !== null) {
            clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = window.setTimeout(() => {
            callback(...args);
        }, delay);
    };

    return debouncedFunction;
};

const parseTitleResponse = (response: TitleSearchResponse): SearchResults[] => {
    let searchResults: SearchResults[] = [];

    if (response.results) {
        response.results.forEach((item) => {
            item.resolutions.forEach((res) => {
                searchResults.push({
                    Name: res.name,
                    Id: res.id,
                    Confidence: res.confidence,
                    Users: res.users,
                    Type: res.additionalData.ProductType,
                    //@ts-ignore
                    AdditionalData: {
                        ...res.additionalData,
                        ProductNames: res.additionalData.ProductNames,
                    },
                });
            });
        });
    }
    return searchResults;
};

const parseFilterResponse = (
    response: FilterSearchResponse
): SearchResults[] => {
    let searchResults: SearchResults[] = [];

    if (response.results) {
        response.results.forEach((item) => {
            searchResults.push({
                Name: item.name,
                Type: item.type,
            });
        });
    }
    return searchResults;
};

const parseProductResponse = (
    response: ProductSearchResponse
): SearchResults[] => {
    let searchResults: SearchResults[] = [];

    if (response.results) {
        response.results.forEach((item) => {
            searchResults.push({
                Name: item.name,
                Type: item.type,
                Id: item.productId,
                ProductType: item.productType,
            });
        });
    }
    return searchResults;
};

const parseSemanticResponse = (
    response: SemanticSearchResponse
): SearchResults[] => {
    let searchResults: SearchResults[] = [];

    response.titlesMapped.forEach((item) => {
        searchResults.push({
            Name: item.Name,
            Id: item.Id,
            Confidence: item.Confidence,
            Users: item.Users,
            Type: 'Title',
        });
    });
    return searchResults;
};

const TitleSelect: React.FC = () => {
    const dispatch = useDispatch();
    const classes = useStyles();
    //const [open, setOpen] = React.useState(false);
    const { isDialogOpen, dialogSearchString, searchTabString, context } = useSelector(
        (state: RootState) => state.settings
    );
    const [searchQuery, setSearchQuery] = React.useState<string>('');
    const [searchResults, setSearchResults] = React.useState<SearchResults[]>(
        []
    );
    const [loading, setLoading] = React.useState<boolean>(false);
    const [cachedResults, setCachedResults] = React.useState<
        Record<string, SearchResults[]>
    >({});

    const user = useContext(UserContext);

    /*const [selectedTab, setSelectedTab] =
        React.useState<TabValue>(searchTabString);*/

    // Handle Select change
    const onTabChange = (event: SelectTabEvent, data: SelectTabData) => {
        dispatch(setSelectedTab(data.value));
        // Re-run the search query when the tab changes
        if (searchQuery) {
            //debouncedApiCall(searchQuery, data.value);
            //@ts-ignore
            callApi(searchQuery, data.value);
        }
    };

    /*(event: SelectTabEvent, data: SelectTabData) => {
        setSelectedValue(data.value);
      };*/

    /*const key = 'HaloTitle';
    const value: SearchResults = {
        Name: 'Halo',
        Type: 'Title',
    };*/

    const getAccessToken = useTokenService();
    const copilotService = CopilotService.getInstance(getAccessToken);

    const callApi = async (query: string, category?: string) => {
        var target = category ?? searchTabString;
        setLoading(true);

        if (target === 'semantic') {
            copilotService
                .titleResolveAPI(query, 10, 'Title')
                //@ts-ignore
                .then((response) => {
                    if (response) {
                        //setSearchResults(response.results[0].resolutions);
                        setSearchResults(parseSemanticResponse(response));
                    }
                    setLoading(false);
                });
        } else if (target === 'title') {
            copilotService
                .searchAPI(query, 30, target)
                //@ts-ignore
                .then((response) => {
                    if (response) {
                        //setSearchResults(response.results[0].resolutions);
                        setSearchResults(parseTitleResponse(response));
                    }
                    setLoading(false);
                });
        } else if (target === 'products') {
            copilotService
                //@ts-ignore
                .productSearchAPI(query, 40, target)
                //@ts-ignore
                .then((response) => {
                    if (response) {
                        setSearchResults(parseProductResponse(response));
                    }
                    setLoading(false);
                });
        } else if (target === 'subscriptions') {
            copilotService
                //@ts-ignore
                .productSearchAPI(query, 40, target)
                //@ts-ignore
                .then((response) => {
                    if (response) {
                        setSearchResults(parseProductResponse(response));
                    }
                    setLoading(false);
                });
        } else {
            copilotService
                //@ts-ignore
                .filterSearchAPI(query, 10, target)
                //@ts-ignore
                .then((response) => {
                    if (response) {
                        setSearchResults(parseFilterResponse(response));
                    }
                    setLoading(false);
                });
        }
    };

    const debouncedApiCall = useDebounce(callApi, 500); // Adjust the delay as needed

    const onSearchChange = (
        event: SearchBoxChangeEvent,
        data: { value: string }
    ) => {
        setSearchQuery(data.value);
        debouncedApiCall(data.value);
    };

    const handleClearContext = () => {
        dispatch(clearContext());
    };

    const handleRemoveContextItem = (key: string) => {
        dispatch(removeFromContext(key));
    };

    useEffect(() => {
        if (isDialogOpen) {
            // Trigger the search request with dialogSearchString
            //console.log('Search for:', dialogSearchString);
            if (dialogSearchString !== '') {
                setSearchQuery(dialogSearchString.replace(/%20/g, ' '));
                debouncedApiCall(dialogSearchString);
            }
            // Replace console.log with actual search logic
        }
    }, [isDialogOpen, dialogSearchString]);

    return (
        <Dialog
            open={isDialogOpen}
            onOpenChange={(event, data) =>
                dispatch(toggleDialog({ isOpen: data.open }))
            }
        >
            <DialogTrigger disableButtonEnhancement>
                <Button
                    appearance="primary"
                    icon={<AddRegular />}
                    disabled={false}
                >
                    <Body1Strong>Add Context</Body1Strong>
                </Button>
            </DialogTrigger>
            <DialogSurface className={classes.surface}>
                <DialogTitle className={classes.header}>
                    <Title2>Add Context To Session</Title2>
                </DialogTitle>
                <DialogBody style={{ padding: '24px' }}>
                    <DialogContent className={classes.container}>
                        <div className={classes.content}>
                           {/* Left Section - Search */}
                           <div className={classes.searchSection}>
                               <TabList
                                   defaultSelectedValue={searchTabString}
                                   onTabSelect={onTabChange}
                               >
                                   {options.map((option) => (
                                       <Tab key={option.key} value={option.key}>
                                           {option.text}
                                       </Tab>
                                   ))}
                               </TabList>
                               <div className={classes.searchBoxContainer}>
                                   <SearchBox
                                       appearance="filled-darker"
                                       placeholder="Search for games, products, or content..."
                                       value={searchQuery}
                                       onChange={onSearchChange}
                                       size="large"
                                       defaultValue={dialogSearchString.replace(
                                           /%20/g,
                                           ' '
                                       )}
                                       className={classes.searchBox}
                                   />
                               </div>
                               <div className={classes.searchResultsContainer}>
                                   {searchTabString === 'products' &&
                                   !user?.hasFinAccess ? (
                                       <Card className={classes.card}>
                                           <Title3>Access Denied</Title3>
                                           <Body2>
                                               You do not have sufficient
                                               permissions to search for products.
                                           </Body2>
                                       </Card>
                                   ) : loading ? (
                                       <div className={classes.skeletonGrid}>
                                           {Array.from({ length: 8 }).map((_, index) => (
                                               <div key={index} className={classes.skeletonCard}>
                                                   <Skeleton aria-label="Loading Content">
                                                       <SkeletonItem
                                                           className={classes.skeletonImage}
                                                           shape="square"
                                                       />
                                                       <div className={classes.skeletonContent}>
                                                           <SkeletonItem size={16} />
                                                           <SkeletonItem size={12} />
                                                       </div>
                                                   </Skeleton>
                                               </div>
                                           ))}
                                       </div>
                                   ) : searchResults &&
                                     searchResults.length > 0 ? (
                                       searchResults.map((result) => (
                                           <SearchResultItem
                                               key={result.Id ?? result.Name}
                                               result={result}
                                           ></SearchResultItem>
                                       ))
                                   ) : (
                                       <Card className={classes.howToUse}>
                                           <Title3>How To Use:</Title3>
                                           <Body2>
                                               Search database and add 'context'
                                               for accurate segment building.
                                           </Body2>
                                           <Body2>Search for</Body2>
                                           <ul>
                                               <li>
                                                   <Body1>Game Titles</Body1>
                                               </li>
                                               <li>
                                                   <Body1>Game Series</Body1>
                                               </li>
                                               <li>
                                                   <Body1>Game Franchises</Body1>
                                               </li>
                                               <li>
                                                   <Body1>Products</Body1>
                                               </li>
                                               <li>
                                                   <Body1>Subscriptions</Body1>
                                               </li>
                                           </ul>
                                       </Card>
                                   )}
                               </div>
                           </div>

                           {/* Right Section - Context Panel */}
                           <div className={classes.contextPanel}>
                               <div className={classes.contextHeader}>
                                   <div className={classes.contextTitle}>
                                       <Text weight="semibold">Session Context</Text>
                                       <Badge appearance="filled" color="brand">
                                           {Object.keys(context).length}
                                       </Badge>
                                       <Tooltip
                                           content="Adding context helps ensure accurate results by associating your question with specific and identifiable products."
                                           relationship="description"
                                       >
                                           <Info12Regular style={{ cursor: 'help', color: tokens.colorNeutralForeground3 }} />
                                       </Tooltip>
                                   </div>
                               </div>
                               
                               <div className={classes.contextList}>
                                   {Object.entries(context)
                                       .sort(([, a], [, b]) => (a.addedAt || 0) - (b.addedAt || 0))
                                       .map(([key, item]) => (
                                           <div key={key} className={classes.contextItem}>
                                               <Text className={classes.contextItemName} title={item.DisplayName}>
                                                   {item.DisplayName}
                                               </Text>
                                               <Button
                                               appearance="subtle"
                                               icon={<Dismiss16Regular />}
                                               size="small"
                                               className={classes.removeButton}
                                               onClick={() => handleRemoveContextItem(key)}
                                               aria-label={`Remove ${item.DisplayName}`}
                                           />
                                       </div>
                                   ))}
                                   {Object.keys(context).length === 0 && (
                                       <Text style={{ textAlign: 'center', color: tokens.colorNeutralForeground3, fontStyle: 'italic' }}>
                                           No items in your context yet
                                       </Text>
                                   )}
                               </div>

                               {Object.keys(context).length > 0 && (
                                   <Button
                                       appearance="outline"
                                       icon={<Delete16Regular />}
                                       onClick={handleClearContext}
                                       style={{ width: '100%' }}
                                   >
                                       Clear All Context
                                   </Button>
                               )}
                           </div>
                       </div>
                        <div
                            style={{
                                display: 'flex',
                                width: '100%',
                                justifyContent: 'flex-end',
                            }}
                        >
                            <DialogTrigger>
                                <Button
                                    appearance="primary"
                                    style={{ padding: '5px 64px' }}
                                >
                                    Close
                                </Button>
                            </DialogTrigger>
                        </div>
                    </DialogContent>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};

export default TitleSelect;
